import axios from 'axios';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import Popup from 'reactjs-popup';
import { useMobileApp } from '../../hooks/useMobileApp';
import { EventInterface } from '../../interfaces/interfaces';
import { MediaComponentInterface } from '../../interfaces/pagebuilder';
import { createDisplayLink } from '../../services/helper';
import PublicMediaComponent from './PublicMediaComponent';
import { MediaDownloadButton } from './MediaDownloadButton';

const VyPdfViewer = dynamic(() => import('../common/VyPdfViewer'), {
  ssr: false,
  loading: () => <p>loading...</p>,
});

function MediaType({ event, uiComponent }: { event: EventInterface; uiComponent?: MediaComponentInterface }) {
  const [media, setMedia] = useState<any>([]);
  const router = useRouter();
  const { t } = useTranslation('pagebuilder');
  const { isAppView } = useMobileApp();

  useEffect(() => {
    if (!uiComponent)
      axios.get(`media/getall/${event._id}`).then(({ data }) => {
        setMedia(data.data);
      });
  }, []);

  useEffect(() => {
    if (uiComponent) setMedia(uiComponent.media);
  }, [uiComponent]);

  function openPopup(media: any) {
    if (!media) return;

    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, ...{ openId: media._id } },
      },
      `${router.asPath}?openId=${media._id}`,
      { shallow: true }
    );
  }

  return (
    <div>
      {media.length > 0 ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 335px)',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
            className="w-full">
            {media
              .slice()
              .sort((a: any, b: any) => a.sortIndex - b.sortIndex)
              .filter((m: any) => m.mediaObject)
              .map((m: any) => m.mediaObject)
              .map((m: any) => (
                <div key={m?._id} className="">
                  <Popup
                    modal
                    lockScroll
                    onOpen={() => openPopup(m)}
                    onClose={() => {
                      const newQuery: any = router.query;
                      delete newQuery.openId;
                      router.push(
                        {
                          pathname: router.pathname,
                          query: newQuery,
                        },
                        router.asPath.substr(0, router.asPath.indexOf('?')),
                        { shallow: true }
                      );
                    }}
                    closeOnEscape
                    defaultOpen={router.query.openId === m?._id}
                    contentStyle={{ overflow: 'scroll', width: 'max-content', maxHeight: '80vh', maxWidth: '80vw' }}
                    trigger={
                      <div>
                        <PublicMediaComponent
                          media={m}
                          textColor={uiComponent?.styling.textColor}
                          showTitles={uiComponent?.showTitles}
                          backgroundColor={uiComponent?.customStyling?.backgroundColor}
                        />
                      </div>
                    }>
                    <div className="flex flex-col space-y-2 modal">
                      <div className="relative h-full">
                        <div className="w-full h-full">
                          {m.type === 'vystemvideo' && m.videoApiId && !m.muxId ? (
                            <ReactPlayer
                              playsinline={isAppView()}
                              width="100%"
                              height="100%"
                              className="h-full overflow-hidden"
                              controls
                              config={{
                                file: {
                                  attributes: {
                                    onContextMenu: (e: any) => e.preventDefault(),
                                  },
                                },
                              }}
                              url={`https://cdn.api.video/vod/${m.videoApiId}/hls/manifest.m3u8`}
                            />
                          ) : m.type === 'pdf' ? (
                            <VyPdfViewer url={createDisplayLink(m.file)}></VyPdfViewer>
                          ) : m.type === 'image' ? (
                            <img src={createDisplayLink(m.file)} className="w-full h-auto"></img>
                          ) : m.type === 'vystemvideo' && m.muxId && m.playback_ids?.length === 1 ? (
                            <ReactPlayer
                              playsinline={isAppView()}
                              className="h-full"
                              width="100%"
                              height="100%"
                              controls
                              url={`https://stream.mux.com/${m.playback_ids[0]?.id}.m3u8`}
                            />
                          ) : (m.type === 'meetingRecording' || m.type === 'streamRecording') && m.muxId && m.playback_ids?.length === 1 ? (
                            <ReactPlayer
                              playsinline={isAppView()}
                              className="h-full"
                              width="100%"
                              height="100%"
                              controls
                              url={`https://stream.mux.com/${m.playback_ids[0]?.id}.m3u8`}
                            />
                          ) : !!m.path ? (
                            <ReactPlayer playsinline={isAppView()} className="h-full" width="100%" height="100%" controls url={m.path} />
                          ) : (
                            <>Invalid Media</>
                          )}
                        </div>
                        {uiComponent?.showTitles && (
                          <div>
                            {m.name && <h2>{m.name}</h2>}
                            {m.author && <p>{m.author}</p>}
                            {m.authorposition && <p style={{ color: 'grey' }}>{m.authorposition !== m.author && m.authorposition}</p>}
                            {m.description && <p style={{ marginTop: '1rem', overflow: 'auto' }}>{m.description}</p>}
                          </div>
                        )}
                        {uiComponent?.media?.find((componentMedia: any) => componentMedia.mediaObject._id === m._id)?.isDownloadable && (
                          <div className="sticky bottom-0 right-0 left-0 ">
                            <MediaDownloadButton media={m} />
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </div>
              ))}
          </div>
        </>
      ) : (
        <div className="grid w-full place-items-center">{t('pagebuilder:no-media-added')}</div>
      )}
    </div>
  );
}

export default MediaType;
